import {
  LIST_REQUEST, LIST_SUCCESS, LIST_FAILURE,
  GET_REQUEST, GET_SUCCESS, GET_FAILURE,
  CANCEL_REQUEST, CANCEL_SUCCESS, CANCEL_FAILURE,
  RETRY_REQUEST, RETRY_SUCCESS, RETRY_FAILURE,
  STATS_REQUEST, STATS_SUCCESS, STATS_FAILURE,
  CLEAR
} from '../../actions/ingestion/executions';

// All non-terminal statuses an execution can be in. Anything in this set
// means "still doing work, keep polling and don't hide cancel/retry buttons".
// `syncing` / `indexing` are kept for backward compat with executions
// created before the ClickHouse migration; new uploads only ever use
// `queued -> processing -> completed`.
const ACTIVE_STATUSES = ['queued', 'processing', 'syncing', 'indexing', 'ingesting', 'pending'];
const isActiveStatus = (s) => ACTIVE_STATUSES.includes(s);

const initialState = {
  // List state
  executions: [],
  pagination: {
    page: 1,
    per_page: 50,
    total: 0,
    total_pages: 0,
    sort_column: 'started_at',
    sort_direction: 'desc'
  },
  isLoading: false,
  error: null,
  
  // Individual execution details
  executionDetails: {},
  isLoadingDetails: false,
  detailsError: null,
  
  // Cancel state
  isCancelling: false,
  cancelError: null,
  cancelledExecutionId: null,
  
  // Retry state
  isRetrying: false,
  retryError: null,
  retryResult: null,
  
  // Stats state
  stats: null,
  isLoadingStats: false,
  statsError: null,
  
  // Active executions for real-time monitoring
  activeExecutions: []
};

export const reducer = (state = initialState, action) => {
  switch (action.type) {
    // List executions
    case LIST_REQUEST:
      return {
        ...state,
        isLoading: true,
        error: null
      };
    
    case LIST_SUCCESS:
      const executions = action.payload.executions || action.payload.data || action.payload;
      const pagination = action.payload.pagination || {
        ...state.pagination,
        total: action.payload.total || executions.length
      };
      return {
        ...state,
        isLoading: false,
        executions,
        pagination,
        error: null,
        activeExecutions: executions
          .filter(exec => isActiveStatus(exec.status))
          .map(exec => exec.execution_id),
      };
    
    case LIST_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.payload
      };
    
    // Get execution details
    case GET_REQUEST:
      return {
        ...state,
        isLoadingDetails: true,
        detailsError: null
      };
    
    case GET_SUCCESS:
      const execution = action.payload.execution || action.payload;
      return {
        ...state,
        isLoadingDetails: false,
        detailsError: null,
        executionDetails: {
          ...state.executionDetails,
          [execution.execution_id]: execution
        },
        // Update execution in list if it exists
        executions: state.executions.map(exec =>
          exec.execution_id === execution.execution_id ? execution : exec
        ),
        activeExecutions: isActiveStatus(execution.status)
          ? [...new Set([...state.activeExecutions, execution.execution_id])]
          : state.activeExecutions.filter(id => id !== execution.execution_id)
      };
    
    case GET_FAILURE:
      return {
        ...state,
        isLoadingDetails: false,
        detailsError: action.payload
      };
    
    // Cancel execution
    case CANCEL_REQUEST:
      return {
        ...state,
        isCancelling: true,
        cancelError: null,
        cancelledExecutionId: null
      };
    
    case CANCEL_SUCCESS:
      return {
        ...state,
        isCancelling: false,
        cancelledExecutionId: action.payload.executionId,
        cancelError: null
      };
    
    case CANCEL_FAILURE:
      return {
        ...state,
        isCancelling: false,
        cancelError: action.payload
      };
    
    // Retry execution
    case RETRY_REQUEST:
      return {
        ...state,
        isRetrying: true,
        retryError: null,
        retryResult: null
      };
    
    case RETRY_SUCCESS:
      return {
        ...state,
        isRetrying: false,
        retryResult: action.payload,
        retryError: null
      };
    
    case RETRY_FAILURE:
      return {
        ...state,
        isRetrying: false,
        retryError: action.payload
      };
    
    // Get stats
    case STATS_REQUEST:
      return {
        ...state,
        isLoadingStats: true,
        statsError: null
      };
    
    case STATS_SUCCESS:
      return {
        ...state,
        isLoadingStats: false,
        stats: action.payload.stats || action.payload,
        statsError: null
      };
    
    case STATS_FAILURE:
      return {
        ...state,
        isLoadingStats: false,
        statsError: action.payload
      };
    
    // Clear state
    case CLEAR:
      return initialState;
    
    default:
      return state;
  }
};
